import { Tables } from "@/supabase/types"
import {
	ChatMessage,
  ChatSettings,
  LLM,
  OpenRouterLLM,
  TenantUser,
  WorkspaceImage
} from "@/types/base"
import Authuser from '@/types/base/authuser'
import { AssistantImage } from "@/types/images/assistant-image"
import { Dispatch, RefObject, SetStateAction, createContext } from "react"
import { SettingsState } from "@/lib/settings/settings-state"
import { SessionRealtimePresenceState, UserSession } from "@/config/config"
import { FolderWithElements } from "@/types/base/folder_with_elements"
import { PermAction } from "@/types/base/permissions"
import { ModelRecordWithProviderName } from "@/db/models"

interface ChatbotUIContext {
  isInitialized: boolean
  setInitialized: Dispatch<SetStateAction<boolean>>

  // PROFILE STORE
  profile: Tables<"profiles"> | null
  setProfile: Dispatch<SetStateAction<Tables<"profiles"> | null>>

  // USER SETTINGS
  settings: SettingsState | null
  setSettings: Dispatch<SetStateAction<SettingsState | null>>

	// USER SESSION
	userSession: UserSession | null
	setUserSession: Dispatch<SetStateAction<UserSession | null>>

  // USER SESSIONS
	sessions: SessionRealtimePresenceState | null
	setSessions: Dispatch<SetStateAction<SessionRealtimePresenceState | null>>

  // AUTH USER & PROFILE
  authuser: Authuser | null
  setAuthuser: Dispatch<React.SetStateAction<Authuser | null>>

  // USERS STORE
  users: TenantUser[]
  setUsers: Dispatch<SetStateAction<TenantUser[]>>

  // ITEMS STORE
  assistants: Tables<"assistants">[]
  setAssistants: Dispatch<SetStateAction<Tables<"assistants">[]>>
  collections: Tables<"collections">[]
  setCollections: Dispatch<SetStateAction<Tables<"collections">[]>>
	chats: Tables<"chats">[]
	setChats: Dispatch<SetStateAction<Tables<"chats">[]>>
  chatIds: string[]
	chatsMap: Map<string, Tables<"chats">>
  setChatIds: Dispatch<SetStateAction<string[]>>
	setChatsMap: Dispatch<SetStateAction<Map<string, Tables<"chats">>>>
  files: Tables<"files">[]
  setFiles: Dispatch<SetStateAction<Tables<"files">[]>>
  folders: FolderWithElements[]
  setFolders: Dispatch<SetStateAction<FolderWithElements[]>>
  mcps: Tables<"mcps">[]
  setMcps: Dispatch<SetStateAction<Tables<"mcps">[]>>
  models: ModelRecordWithProviderName[]
  setModels: Dispatch<SetStateAction<ModelRecordWithProviderName[]>>
  presets: Tables<"presets">[]
  setPresets: Dispatch<SetStateAction<Tables<"presets">[]>>
  profiles: Tables<"profiles">[]
  setProfiles: Dispatch<SetStateAction<Tables<"profiles">[]>>
  prompts: Tables<"prompts">[]
  setPrompts: Dispatch<SetStateAction<Tables<"prompts">[]>>
  providers: Tables<"providers">[]
  setProviders: Dispatch<SetStateAction<Tables<"providers">[]>>
  tools: Tables<"tools">[]
  setTools: Dispatch<SetStateAction<Tables<"tools">[]>>
  tenants: Tables<"tenants">[]
  setTenants: Dispatch<SetStateAction<Tables<"tenants">[]>>
  roles: Tables<"roles">[]
  setRoles: Dispatch<SetStateAction<Tables<"roles">[]>>
  groups: Tables<"groups">[]
  setGroups: Dispatch<SetStateAction<Tables<"groups">[]>>
  workspaces: Tables<"workspaces">[]
  setWorkspaces: Dispatch<SetStateAction<Tables<"workspaces">[]>>

  // MODELS STORE
  envKeyMap: Record<string, boolean>
  setEnvKeyMap: Dispatch<SetStateAction<Record<string, boolean>>>
  tenantKeyMap: Record<string, boolean>
  setTenantKeyMap: Dispatch<SetStateAction<Record<string, boolean>>>
  tenantBillableKeyMap: Record<string, boolean>
  setTenantBillableKeyMap: Dispatch<SetStateAction<Record<string, boolean>>>
  tenantSharedKeyMap: Record<string, boolean>
  setTenantSharedKeyMap: Dispatch<SetStateAction<Record<string, boolean>>>
	allModels: LLM[]
  availableHostedModels: LLM[]
  setAvailableHostedModels: Dispatch<SetStateAction<LLM[]>>
  availableLocalModels: LLM[]
  setAvailableLocalModels: Dispatch<SetStateAction<LLM[]>>
  availableOpenRouterModels: OpenRouterLLM[]
  setAvailableOpenRouterModels: Dispatch<SetStateAction<OpenRouterLLM[]>>

  // WORKSPACE STORE
  selectedWorkspace: Tables<"workspaces"> | null
  setSelectedWorkspace: Dispatch<SetStateAction<Tables<"workspaces"> | null>>
  workspaceImages: WorkspaceImage[]
  setWorkspaceImages: Dispatch<SetStateAction<WorkspaceImage[]>>

  // PRESET STORE
  selectedPreset: Tables<"presets"> | null
  setSelectedPreset: Dispatch<SetStateAction<Tables<"presets"> | null>>

  // ASSISTANT STORE
  selectedAssistant: Tables<"assistants"> | null
  setSelectedAssistant: Dispatch<SetStateAction<Tables<"assistants"> | null>>
  assistantImages: AssistantImage[]
  setAssistantImages: Dispatch<SetStateAction<AssistantImage[]>>
  openaiAssistants: any[]
  setOpenaiAssistants: Dispatch<SetStateAction<any[]>>

  // PASSIVE CHAT STORE
  chatSettings: ChatSettings | null
  setChatSettings: Dispatch<SetStateAction<ChatSettings>>
  selectedChat: Tables<"chats"> | null
  setSelectedChat: Dispatch<SetStateAction<Tables<"chats"> | null>>
  showDashboard: boolean
  setShowDashboard: Dispatch<SetStateAction<boolean>>

	// REFS
  workspaceIdRef: RefObject<string | null>
  selectedChatIdRef: RefObject<string | null>
  authuserRef: RefObject<Authuser | null>
  tenantIdRef: RefObject<string | null>
	chatMessagesRef: RefObject<ChatMessage[]>
	setChatMessagesRef: RefObject<Dispatch<SetStateAction<ChatMessage[]>>>
	setChatFileItemsRef: RefObject<Dispatch<SetStateAction<string[]>>>

	getTenantId: () => string | null

  validateChats: (chat: Tables<"chats">, action: PermAction) => string | null
  validatePresets: (preset: Tables<"presets">, action: PermAction) => string | null
  validateProfiles: (file: Tables<"profiles">, action: PermAction) => string | null
  validatePrompts: (prompt: Tables<"prompts">, action: PermAction) => string | null
  validateProviders: (prompt: Tables<"providers">, action: PermAction) => string | null
  validateFiles: (file: Tables<"files">, action: PermAction) => string | null
  validateCollections: (collection: Tables<"collections">, action: PermAction) => string | null
  validateAssistants: (assistant: Tables<"assistants">, action: PermAction) => string | null
  validateTenants: (tenant: Tables<"tenants">, action: PermAction) => string | null
  validateRoles: (role: Tables<"roles">, action: PermAction) => string | null
  validateGroups: (group: Tables<"groups">, action: PermAction) => string | null
  validateTools: (tool: Tables<"tools">, action: PermAction) => string | null
  validateMcps: (mcp: Tables<"mcps">, action: PermAction) => string | null
  validateModels: (model: Tables<"models">, action: PermAction) => string | null

	isSharedConfig: (elementSharing: string, owningTenant: string) => Promise<boolean>
	isProviderKeyActive: (provider: string) => boolean
}

export const ChatbotUIContext = createContext<ChatbotUIContext>({
  isInitialized: false,
	setInitialized: () => {},

  // PROFILE STORE
  profile: null,
  setProfile: () => {},

  // USER SETTINGS
  settings: null,
  setSettings: () => {},

	// USER SESSION
	userSession: null,
	setUserSession: () => {},

	// USER SESSIONS
	sessions: null,
	setSessions: () => {},

  // AUTH USER
  authuser: null,
  setAuthuser: () => {},

  // USERS STORE
  users: [],
  setUsers: () => {},

  // ITEMS STORE
  assistants: [],
  setAssistants: () => {},
  collections: [],
  setCollections: () => {},
	chats: [],
  setChats: () => {},
  chatIds: [],
	chatsMap: new Map(),
  setChatIds: () => {},
	setChatsMap: () => {},
  files: [],
  setFiles: () => {},
  folders: [],
  setFolders: () => {},
  mcps: [],
  setMcps: () => {},
  models: [],
  setModels: () => {},
  presets: [],
  setPresets: () => {},
  profiles: [],
  setProfiles: () => {},
  prompts: [],
  setPrompts: () => {},
  providers: [],
  setProviders: () => {},
  tools: [],
  setTools: () => {},
  tenants: [],
  setTenants: () => {},
  roles: [],
  setRoles: () => {},
  groups: [],
  setGroups: () => {},
  workspaces: [],
  setWorkspaces: () => {},

  // MODELS STORE
  envKeyMap: {},
  setEnvKeyMap: () => {},
  tenantKeyMap: {},
  setTenantKeyMap: () => {},
  tenantBillableKeyMap: {},
  setTenantBillableKeyMap: () => {},
  tenantSharedKeyMap: {},
  setTenantSharedKeyMap: () => {},
  allModels: [],
  availableHostedModels: [],
  setAvailableHostedModels: () => {},
  availableLocalModels: [],
  setAvailableLocalModels: () => {},
  availableOpenRouterModels: [],
  setAvailableOpenRouterModels: () => {},

  // WORKSPACE STORE
  selectedWorkspace: null,
  setSelectedWorkspace: () => {},
  workspaceImages: [],
  setWorkspaceImages: () => {},

  // PRESET STORE
  selectedPreset: null,
  setSelectedPreset: () => {},

  // ASSISTANT STORE
  selectedAssistant: null,
  setSelectedAssistant: () => {},
  assistantImages: [],
  setAssistantImages: () => {},
  openaiAssistants: [],
  setOpenaiAssistants: () => {},

  // PASSIVE CHAT STORE
  selectedChat: null,
  setSelectedChat: () => {},
  chatSettings: null,
  setChatSettings: () => {},
  showDashboard: false,
  setShowDashboard: () => {},

	// REFS
	workspaceIdRef: { current: null } as RefObject<string | null>,
  selectedChatIdRef: { current: null } as RefObject<string | null>,
  authuserRef: { current: null } as RefObject<Authuser | null>,
  tenantIdRef: { current: null } as RefObject<string | null>,
	chatMessagesRef: { current: [] } as RefObject<ChatMessage[]>,
	setChatMessagesRef: { current: () => {} } as RefObject<Dispatch<SetStateAction<ChatMessage[]>>>,
	setChatFileItemsRef: { current: () => {} } as RefObject<Dispatch<SetStateAction<string[]>>>,

	getTenantId: () => null,

  validateChats: (_chat: Tables<"chats">, _action: PermAction) => null,
  validatePresets: (_preset: Tables<"presets">, _action: PermAction) => null,
  validateProfiles: (_prompt: Tables<"profiles">, _action: PermAction) => null,
  validatePrompts: (_prompt: Tables<"prompts">, _action: PermAction) => null,
  validateProviders: (_provider: Tables<"providers">, _action: PermAction) => null,
  validateFiles: (_file: Tables<"files">, _action: PermAction) => null,
  validateCollections: (_collection: Tables<"collections">, _action: PermAction) => null,
  validateAssistants: (_assistant: Tables<"assistants">, _action: PermAction) => null,
  validateTenants: (_tenant: Tables<"tenants">, _action: PermAction) => null,
  validateRoles: (_role: Tables<"roles">, _action: PermAction) => null,
  validateGroups: (_group: Tables<"groups">, _action: PermAction) => null,
  validateTools: (_tool: Tables<"tools">, _action: PermAction) => null,
  validateMcps: (_mcp: Tables<"mcps">, _action: PermAction) => null,
  validateModels: (_model: Tables<"models">, _action: PermAction) => null,

	isSharedConfig: async (_sharing: string, _owningTenant: string) => false,
	isProviderKeyActive: (_provider: string) => false,
})
